import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface SuccessModalProps {
  open: boolean;
  onClose: () => void;
  projectData: {
    title: string;
    description: string;
    project_type: string;
    valuation_usd: number;
    tokenized_percent: number;
    location?: string;
    token_name?: string;
    token_symbol?: string;
  };
}

const SuccessModal: React.FC<SuccessModalProps> = ({ open, onClose, projectData }) => {
  const projectTypes = [
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'art', label: 'Art' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'agriculture', label: 'Agriculture' },
  ];

  const getProjectTypeLabel = (value: string) => {
    return projectTypes.find(t => t.value === value)?.label || value;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckIcon sx={{ mr: 1, color: 'success.main' }} />
          Project Submitted Successfully!
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              Congratulations!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your project has been submitted successfully and is now under review.
            </Typography>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Project Details
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Project Title
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {projectData.title}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Project Type
                  </Typography>
                  <Chip 
                    label={getProjectTypeLabel(projectData.project_type)}
                    color="primary"
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {projectData.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Valuation
                  </Typography>
                  <Typography variant="body1" gutterBottom color="primary.main" fontWeight={600}>
                    ${projectData.valuation_usd?.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Tokenized Percentage
                  </Typography>
                  <Typography variant="body1" gutterBottom color="success.main" fontWeight={600}>
                    {projectData.tokenized_percent}%
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {projectData.location || 'Not specified'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

                     <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
             <Card sx={{ flex: 1, textAlign: 'center', minHeight: 120 }}>
               <CardContent sx={{ p: 2 }}>
                 <AssignmentIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                 <Typography variant="body2" color="text.secondary" gutterBottom>
                   Under Review
                 </Typography>
                 <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                   Our team will review your project within 24-48 hours
                 </Typography>
               </CardContent>
             </Card>
             
             <Card sx={{ flex: 1, textAlign: 'center', minHeight: 120 }}>
               <CardContent sx={{ p: 2 }}>
                 <SecurityIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                 <Typography variant="body2" color="text.secondary" gutterBottom>
                   Compliance Check
                 </Typography>
                 <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                   AI-powered compliance verification in progress
                 </Typography>
               </CardContent>
             </Card>
             
             <Card sx={{ flex: 1, textAlign: 'center', minHeight: 120 }}>
               <CardContent sx={{ p: 2 }}>
                 <TrendingUpIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                 <Typography variant="body2" color="text.secondary" gutterBottom>
                   Tokenization Ready
                 </Typography>
                 <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                   Will be tokenized upon approval
                 </Typography>
               </CardContent>
             </Card>
           </Box>

          <Box sx={{ p: 2, bgcolor: 'rgba(23, 209, 198, 0.1)', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Next Steps:</strong> You'll receive an email notification once your project has been reviewed. 
              You can also check the status in your issuer dashboard.
            </Typography>
          </Box>
        </motion.div>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Go to Dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SuccessModal; 